import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useTimezone } from '../context/TimezoneContext';
import { ThemedText, Button, Input, TimezonePicker, Avatar, ErrorState } from '../components';
import { Layout } from '../components/Layout';
import userService from '../api/services/userService';
import { useNavigate } from 'react-router-dom';
import QrLoginDisplay from '../components/QrLoginDisplay';
import ChangePasswordCard from '../components/settings/ChangePasswordCard';
import { Pencil, Save, Camera, FileText, Send, CheckCircle, ClipboardList, Calendar, Clock, Activity } from 'lucide-react';

const ProfileContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const ProfileCard = styled.div`
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  border: 1px solid ${props => props.theme.colors.border};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const ProfileHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.xl};
  padding-bottom: ${props => props.theme.spacing.lg};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const AvatarWrapper = styled.div`
  position: relative;
  width: 80px;
  height: 80px;
  cursor: ${props => props.$editable ? 'pointer' : 'default'};
`;

const AvatarOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
  color: white;
  font-size: 20px;

  ${AvatarWrapper}:hover & {
    opacity: ${props => props.$editable ? 1 : 0};
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const RemoveAvatarButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.danger};
  cursor: pointer;
  font-size: ${props => props.theme.typography.sizes.small};
  padding: 0;
  margin-top: ${props => props.theme.spacing.xs};

  &:hover {
    text-decoration: underline;
  }
`;

const ProfileInfo = styled.div`
  flex: 1;
`;

const FormSection = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${props => props.theme.spacing.md};
`;

const StatCard = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  background-color: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.lg};
  border: 1px solid ${props => props.theme.colors.border};
  transition: box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(35, 37, 98, 0.08);
    transform: translateY(-1px);
  }
`;

const StatIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.$bg || props.theme.colors.tint + '14'};
  color: ${props => props.$color || props.theme.colors.tint};
`;

const StatContent = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const StatValue = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  line-height: 1.2;
`;

const StatLabel = styled.div`
  font-size: 13px;
  color: ${props => props.theme.colors.muted};
  line-height: 1.3;
`;

const StatSecondary = styled.div`
  font-size: 11px;
  color: ${props => props.$color || props.theme.colors.muted};
  margin-top: 2px;
  opacity: 0.8;
`;

const StatsLoadingCard = styled(StatCard)`
  @keyframes shimmer {
    0% { opacity: 0.4; }
    50% { opacity: 0.7; }
    100% { opacity: 0.4; }
  }
  animation: shimmer 1.5s ease-in-out infinite;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  justify-content: flex-end;
  margin-top: ${props => props.theme.spacing.xl};
  padding-top: ${props => props.theme.spacing.lg};
  border-top: 1px solid ${props => props.theme.colors.border};
`;

const CertLink = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  background: none;
  border: none;
  color: ${props => props.theme.colors.tint};
  cursor: pointer;
  font-size: ${props => props.theme.typography.sizes.default};
  font-weight: 600;
  padding: ${props => props.theme.spacing.md} 0;

  &:hover {
    text-decoration: underline;
  }
`;

export const ProfileSection = () => {
  const theme = useTheme();
  const { user, updateUser } = useAuth();
  const { browserTimezone } = useTimezone();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const fileInputRef = useRef(null);

  // Form state
  const [formData, setFormData] = useState({
    fullName: user?.fullName || '',
    email: user?.email || '',
    role: user?.role || '',
    company: user?.company || 'aQRate Solutions',
    phone: user?.phone || '',
    location: user?.location || '',
    bio: user?.bio || 'Water quality specialist focused on field data collection and analysis.',
    timezoneId: user?.timezoneId || null
  });

  // Update form data when user changes
  useEffect(() => {
    if (user) {
      setFormData({
        fullName: user.fullName || '',
        email: user.email || '',
        role: user.role || '',
        company: user.company || 'aQRate Solutions',
        phone: user.phone || '',
        location: user.location || '',
        bio: user.bio || '',
        timezoneId: user.timezoneId || null
      });
    }
  }, [user]);

  const handleAvatarClick = () => {
    if (isEditing && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleAvatarFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setSaveError('Please select a valid image file (JPG, PNG, GIF, or WebP).');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setSaveError('Image must be less than 5MB.');
      return;
    }

    // Show local preview immediately
    setAvatarPreview(URL.createObjectURL(file));
    setSaveError(null);
    setIsUploadingAvatar(true);

    try {
      const updatedProfile = await userService.uploadAvatar(file);
      if (updateUser) {
        updateUser({ ...updatedProfile, fullName: `${updatedProfile.firstName} ${updatedProfile.lastName}`.trim() });
      }
      setAvatarPreview(null);
    } catch (error) {
      console.error('Failed to upload avatar:', error);
      setSaveError(error.message || 'Failed to upload avatar.');
      setAvatarPreview(null);
    } finally {
      setIsUploadingAvatar(false);
      // Reset file input so the same file can be selected again
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveAvatar = async () => {
    setIsUploadingAvatar(true);
    setSaveError(null);
    try {
      const updatedProfile = await userService.removeAvatar();
      if (updateUser) {
        updateUser({ ...updatedProfile, fullName: `${updatedProfile.firstName} ${updatedProfile.lastName}`.trim() });
      }
      setAvatarPreview(null);
    } catch (error) {
      console.error('Failed to remove avatar:', error);
      setSaveError(error.message || 'Failed to remove avatar.');
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);
    try {
      // Split full name into first and last name for API
      const nameParts = formData.fullName.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      // Prepare data for API
      const updateData = {
        firstName,
        lastName,
        phone: formData.phone || null,
        location: formData.location || null,
        bio: formData.bio || null,
        company: formData.company || null,
        timezoneId: formData.timezoneId
      };

      // Call API to update profile
      const updatedProfile = await userService.updateUserProfile(updateData);

      // Update user in auth context
      if (updateUser) {
        updateUser({
          ...updatedProfile,
          fullName: formData.fullName,
          timezoneId: formData.timezoneId
        });
      }

      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update profile:', error);
      setSaveError(error.message || 'Failed to save profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      fullName: user?.fullName || '',
      email: user?.email || '',
      role: user?.role || '',
      company: user?.company || 'aQRate Solutions',
      phone: user?.phone || '',
      location: user?.location || '',
      bio: user?.bio || 'Water quality specialist focused on field data collection and analysis.',
      timezoneId: user?.timezoneId || null
    });
    setSaveError(null);
    setIsEditing(false);
  };

  // Activity statistics from API
  const [stats, setStats] = useState(null);
  const [fetchError, setFetchError] = useState(null);

  const fetchStats = async () => {
    setFetchError(null);
    try {
      const data = await userService.getUserStatistics();
      setStats(data);
    } catch (err) {
      console.error('Failed to load activity statistics:', err);
      setFetchError(err?.response?.data?.message || 'Failed to load activity statistics. Please check your connection and try again.');
      setStats('error');
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const formatLastActivity = (dateStr) => {
    if (!dateStr) return 'No submissions yet';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  const getStatCards = () => {
    if (!stats || stats === 'error') return null;
    return [
      { icon: FileText, label: 'Forms Created', value: stats.formsCreated ?? 0, secondary: `${stats.activeForms ?? 0} active`, color: '#232562', bg: '#23256210' },
      { icon: Send, label: 'Total Submissions', value: stats.totalSubmissions ?? 0, secondary: `${stats.submissionsThisMonth ?? 0} this month`, color: '#59caee', bg: '#59caee14' },
      { icon: CheckCircle, label: 'Pending Review', value: stats.pendingReview ?? 0, secondary: stats.pendingReview > 0 ? 'Needs attention' : 'All clear', color: stats.pendingReview > 0 ? '#ffc107' : '#10bb10', bg: stats.pendingReview > 0 ? '#ffc10714' : '#10bb1014' },
      { icon: ClipboardList, label: 'Active Work Orders', value: stats.activeWorkOrders ?? 0, secondary: 'Open & in progress', color: '#8b5cf6', bg: '#8b5cf614' },
      { icon: Calendar, label: 'Last Submission', value: formatLastActivity(stats.lastSubmissionDate), secondary: stats.lastSubmissionDate ? new Date(stats.lastSubmissionDate).toLocaleDateString() : '', color: '#f97316', bg: '#f9731614', isText: true },
      { icon: Activity, label: 'Days Active', value: stats.daysActive ?? 0, secondary: `Since ${user?.createdAt ? new Date(user.createdAt).toLocaleDateString(undefined, { month: 'short', year: 'numeric' }) : 'sign up'}`, color: '#10bb10', bg: '#10bb1014' },
    ];
  };

  const renderStats = () => {
    const cards = getStatCards();
    return (
      <StatsGrid theme={theme}>
        {cards ? cards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <StatCard key={index} theme={theme}>
              <StatIconWrapper theme={theme} $color={stat.color} $bg={stat.bg}>
                <Icon size={20} />
              </StatIconWrapper>
              <StatContent>
                <StatValue theme={theme} style={stat.isText ? { fontSize: '16px', fontWeight: 600 } : undefined}>
                  {stat.value}
                </StatValue>
                <StatLabel>{stat.label}</StatLabel>
                {stat.secondary && <StatSecondary>{stat.secondary}</StatSecondary>}
              </StatContent>
            </StatCard>
          );
        }) : [0, 1, 2, 3, 4, 5].map(i => (
          <StatsLoadingCard key={i} theme={theme}>
            <StatIconWrapper theme={theme} style={{ opacity: 0.3 }}>
              <Clock size={20} />
            </StatIconWrapper>
            <StatContent>
              <StatValue theme={theme} style={{ opacity: 0.3 }}>--</StatValue>
              <StatLabel style={{ opacity: 0.3 }}>Loading...</StatLabel>
            </StatContent>
          </StatsLoadingCard>
        ))}
      </StatsGrid>
    );
  };

  return (
    <>
      <ProfileContainer>
        <ProfileCard theme={theme}>
          <ProfileHeader theme={theme}>
            <div>
              <AvatarWrapper $editable={isEditing} onClick={handleAvatarClick}>
                <Avatar
                  src={avatarPreview || user?.profilePicture}
                  name={formData.fullName || 'User'}
                  size="lg"
                />
                {isEditing && (
                  <AvatarOverlay $editable={isEditing}>
                    {isUploadingAvatar ? '...' : <Camera size={18} />}
                  </AvatarOverlay>
                )}
              </AvatarWrapper>
              <HiddenFileInput
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleAvatarFileChange}
              />
              {isEditing && user?.profilePicture && !isUploadingAvatar && (
                <RemoveAvatarButton theme={theme} type="button" onClick={handleRemoveAvatar}>
                  Remove photo
                </RemoveAvatarButton>
              )}
            </div>
            <ProfileInfo>
              <ThemedText type="title">
                {formData.fullName || 'User Profile'}
              </ThemedText>
              <ThemedText style={{ color: theme.colors.muted }}>
                {formData.role} • {formData.company}
              </ThemedText>
              <ThemedText type="small" style={{ color: theme.colors.muted, marginTop: theme.spacing.sm }}>
                Member since March 2024
              </ThemedText>
            </ProfileInfo>
          </ProfileHeader>

          <FormSection>
            <ThemedText type="subtitle" style={{ marginBottom: theme.spacing.lg }}>
              Personal Information
            </ThemedText>

            <FormRow>
              <Input
                label="Full Name"
                value={formData.fullName}
                onChange={(e) => handleInputChange('fullName', e.target.value)}
                disabled={!isEditing}
              />
              <Input
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                disabled={!isEditing}
              />
            </FormRow>

            <FormRow>
              <Input
                label="Role"
                value={formData.role}
                onChange={(e) => handleInputChange('role', e.target.value)}
                disabled={!isEditing}
              />
              <Input
                label="Company"
                value={formData.company}
                onChange={(e) => handleInputChange('company', e.target.value)}
                disabled={!isEditing}
              />
            </FormRow>

            <FormRow>
              <Input
                label="Phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                disabled={!isEditing}
                placeholder="(555) 123-4567"
              />
              <Input
                label="Location"
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                disabled={!isEditing}
                placeholder="City, State"
              />
            </FormRow>

            <TimezonePicker
              label="Timezone Preference"
              value={formData.timezoneId}
              onChange={(value) => handleInputChange('timezoneId', value)}
              disabled={!isEditing}
            />

            <Input
              label="Bio"
              type="textarea"
              rows={4}
              value={formData.bio}
              onChange={(e) => handleInputChange('bio', e.target.value)}
              disabled={!isEditing}
              placeholder="Tell us about yourself and your work in water quality..."
            />
          </FormSection>

          {saveError && (
            <ThemedText style={{ color: theme.colors.danger, marginBottom: theme.spacing.md }}>
              {saveError}
            </ThemedText>
          )}

          <ActionButtons theme={theme}>
            {!isEditing ? (
              <Button variant="primary" onClick={() => setIsEditing(true)}>
                <Pencil size={14} /> Edit Profile
              </Button>
            ) : (
              <>
                <Button variant="secondary" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : <><Save size={14} /> Save Changes</>}
                </Button>
              </>
            )}
          </ActionButtons>
        </ProfileCard>

        <ChangePasswordCard />

        <ProfileCard theme={theme}>
          <ThemedText type="subtitle" style={{ marginBottom: theme.spacing.lg }}>
            Activity Statistics
          </ThemedText>

          {fetchError ? (
            <ErrorState
              theme={theme}
              title="Failed to load activity statistics"
              message={fetchError}
              onRetry={fetchStats}
            />
          ) : renderStats()}
        </ProfileCard>

        {/* My Certificates Link */}
        <ProfileCard theme={theme}>
          <ThemedText type="subtitle" style={{ marginBottom: theme.spacing.sm }}>
            My Certificates
          </ThemedText>
          <ThemedText type="small" style={{ color: theme.colors.muted, marginBottom: theme.spacing.sm }}>
            Manage your professional certifications and credentials.
          </ThemedText>
          <CertLink theme={theme} onClick={() => navigate('/settings?tab=certificates')}>
            Manage Certificates &rarr;
          </CertLink>
        </ProfileCard>

        <ProfileCard theme={theme}>
          <ThemedText type="subtitle" style={{ marginBottom: theme.spacing.lg }}>
            Mobile Device Login
          </ThemedText>
          <QrLoginDisplay />
        </ProfileCard>
      </ProfileContainer>
    </>
  );
};

export const ProfilePage = () => (
  <Layout currentPath="/profile">
    <ProfileSection />
  </Layout>
);